/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Abfindung-berechnen_700px.jpg";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Paper } from "@mui/material";

const GuidebookSettlementCalculation = () => {
  const title = "Ratgeber - Wie wird eine Abfindung berechnet? | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Ihnen wurde gekündigt und Sie fragen sich, ob Sie mit einer Abfindung rechnen können und wie hoch diese
            ausfallen kann? Wir erklären Ihnen die Berechnung der Abfindung."
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Wie wird eine Abfindung berechnet?"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Ihnen wurde gekündigt und Sie fragen sich, ob Sie mit einer Abfindung rechnen können und wie hoch diese ausfallen kann? Wir erklären Ihnen in Kürze,
            wie eine Abfindung berechnet wird.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Gehalt und Beschäftigungszeit entscheidend
          </Typography>
          <p>
            Die Höhe der Abfindung basiert darauf, wie lange Sie im Unternehmen gearbeitet haben und wie viel Sie monatlich brutto verdienen. In der Regel
            verwendet das Arbeitsgericht folgende Formel für die Berechnung einer sogenannten “Regelabfindung”:{" "}
            <strong>0,5 Bruttomonatsgehälter pro volles Beschäftigungsjahr</strong>.
          </p>
          <HighlightBox headline={"Rechenbeispiel"}>
            <p>Rechnung bei einer Beschäftigungszeit von 5 Jahren und einem monatlichen Bruttogehalt von 4.000 €</p>
            <ul>
              <li>0,5 x 4.000€ monatliches Bruttogehalt = 2000 € Regelabfindung pro Beschäftigungsjahr</li>
              <li>2.000 € x 5 Jahre Beschäftigungszeit = 10.000 € Gesamtregelabfindung</li>
            </ul>
            <p>In diesem Fall würde die gesamte Regelabfindung bei 10.000 € liegen.</p>
          </HighlightBox>
          <p>
            Bei großen, finanzstarken Unternehmen und/oder bei kurzer Beschäftigungsdauer kann in seltenen Fällen auch eine Abfindung von 1,0 Bruttogehältern
            pro Beschäftigungsjahr ausgehandelt werden. Allerdings schlagen je nach Region die Arbeitsgerichte auch routinemäßig nur 0,25 Gehälter pro
            Beschäftigungsjahr vor. Sprich: Die “lokal geltende Regelabfindung” kann variieren. Auch bei kleineren und wenig finanzstarken Unternehmen kann die
            geringere Regelabfindung von 0,25 Gehältern gelten - zum Beispiel bei kleineren Baubetrieben oder Reinigungsunternehmen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was gebe ich beim Gehalt genau an?
          </Typography>
          <p>
            Das Gehalt ist ein entscheidender Faktor bei der Berechnung der Abfindung. Hierbei ist es üblich, das Bruttogehalt als Berechnungsgrundlage zu
            nutzen, d.h. eine Abfindungsberechnung auf der Grundlage eines Nettolohns wäre nicht in Ordnung.
          </p>
          <p>
            Nicht so klar ist dagegen die Antwort auf die Frage, ob Zuschläge, Einmalzahlungen oder regelmäßig anfallende Überstunden in das Monatsgehalt mit
            einzurechnen sind. Um diese Gehaltsbestandteile bei der Abfindung zu berücksichtigen, teilt man am besten das Jahresgehalt, das normalerweise in der
            Dezemberabrechnung des letzten Kalenderjahres ausgewiesen wird, durch zwölf. Dadurch errechnet sich meist ein (deutlich) höheres "Monatsgehalt" als
            wenn man sich die Gehaltsabrechnung des laufenden Monats oder der letzten drei Monate anschaut.
          </p>
          <p>
            Hier gibt es jedoch keine grundsätzlich "richtige" oder "falsche" Berechnungsmethode. Welches Monatsgehalt zugrunde gelegt wird, ist letzten Endes
            Verhandlungssache.
          </p>
          <HighlightBox headline={"Schnell handeln: 3-Wochen-Frist bei Kündigung"}>
            <p>
              Eine Abfindung kann nur 3 Wochen nach Erhalt der Kündigung geltend gemacht werden. Nutzen Sie die Chance auf eine Abfindung und starten Sie
              direkt!
            </p>
          </HighlightBox>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Ihre Abfindung
          </Typography>
          <p>
            Sie wurden gekündigt und möchten mit professioneller Unterstützung das bestmögliche Ergebnis erzielen? Dann machen Sie jetzt in nur 5 Minuten alle
            Angaben zu Ihrer Kündigung und wir prüfen Ihre Kündigung komplett unverbindlich!
          </p>
          <DoubleButton
            buttonText={"Kündigung unverbindlich prüfen"}
            buttonLink={"/servicewelt"}
            backText={"Zurück zum Ratgeber"}
            backLink={"/ratgeber/arbeitsrecht"}
          />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookSettlementCalculation;
